import { useState, useMemo, useCallback, memo } from 'react';
import { CustomerEquipmentTypeResponse, CustomerLocationDto } from '@/types';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Star, Package, ArrowRight, Loader2 } from 'lucide-react';
import { useAddToCart } from '@/hooks/useCart';
import { useLocationStore } from '@/stores/useLocationStore';
import { useToastStore } from '@/stores/useToastStore';
import { cn, getRentalDayCount } from '@/lib/utils';
import { getLowestTierPrice, getTierForDays, hasTieredPricing } from '@/lib/pricing';
import { hasMonthlyRentalPricing, getLowestMonthlyRentalRate } from '@/components/pricing';
import { Link } from 'react-router';
import { LocationRequiredModal } from './LocationRequiredModal';
import { DatePicker } from '@/features/search/components/DatePicker';
import { PricingOptionType } from '@/types/cart';
import { useFormatPrice } from '@/hooks/useFormatPrice';
import { useLocale } from '@/i18n/useLocale';
import { buildPublishedLocaleUrl } from '@/i18n/routing';

interface ProductCardProps {
    product: CustomerEquipmentTypeResponse;
    /** Only catalogue results requested with the selected dates carry a trip-specific stock count. */
    availabilityIsDateScoped?: boolean;
}

export const ProductCard = memo(({ product, availabilityIsDateScoped = false }: ProductCardProps) => {
    const addToCartMutation = useAddToCart();
    const { dateRange, location, setLocation, rentalMode } = useLocationStore();
    const { success, warning } = useToastStore();
    const [showLocationModal, setShowLocationModal] = useState(false);
    const formatPrice = useFormatPrice();

    const hasPricing = useMemo(() => hasTieredPricing(product), [product]);
    const hasMonthlyRental = useMemo(() => hasMonthlyRentalPricing(product), [product]);
    const isUnavailable = useMemo(
        () => !product.isAvailableForRental || (!hasPricing && !hasMonthlyRental),
        [product.isAvailableForRental, hasPricing, hasMonthlyRental]
    );
    // The API defaults to today's stock when dates are absent. That does not
    // establish availability for the customer's intended trip.
    const hasFullContext = !!(location && dateRange.from && dateRange.to && dateRange.to > dateRange.from);
    const hasCheckedDateContext = rentalMode !== 'subscription' && hasFullContext && availabilityIsDateScoped;
    const unavailableForDates = hasCheckedDateContext && product.isAvailableForRental && product.availableQuantity <= 0;
    const cannotAddToCart = isUnavailable || unavailableForDates;
    const isLowStock = hasCheckedDateContext && !cannotAddToCart && product.availableQuantity > 0 && product.availableQuantity <= 3;

    const rentalDays = useMemo(
        () => dateRange.from && dateRange.to ? getRentalDayCount(dateRange.from, dateRange.to) : null,
        [dateRange.from, dateRange.to]
    );

    const tieredLowestPrice = useMemo(() => getLowestTierPrice(product), [product]);
    const lowestPrice = useMemo(
        () => !location ? (product.lowestPrice ?? tieredLowestPrice) : tieredLowestPrice,
        [location, product.lowestPrice, tieredLowestPrice]
    );
    const calculatedTier = useMemo(
        () => rentalDays ? getTierForDays(rentalDays, product) : null,
        [rentalDays, product]
    );

    const secondaryImage = useMemo(
        () => product.imageUrls && product.imageUrls.length > 0 ? product.imageUrls[0] : null,
        [product.imageUrls]
    );
    // Build the link with the active locale prefix. The customer site is
    // locale-prefixed (every route lives under /:locale/...); a bare
    // /product/<id> falls into the splat catchall (NotFoundPage) and the
    // resulting hydration mismatch was bouncing users back to home.
    const localeState = useLocale();
    const linkLocale = localeState?.locale ?? 'en';
    const detailHref = useMemo(
        () => buildPublishedLocaleUrl(linkLocale, `/product/${product.slug || product.id}`),
        [linkLocale, product.slug, product.id],
    );

    const handleAddToCart = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (isUnavailable) {
            warning("This item is currently unavailable for rental.");
            return;
        }

        if (unavailableForDates && location) {
            warning("This item is unavailable for these dates. Try changing your rental dates.");
            return;
        }

        if (!location) {
            setShowLocationModal(true);
            return;
        }

        if (!dateRange.from || !dateRange.to) {
            warning("Please select rental dates first.");
            return;
        }

        addToCartMutation.mutate({
            equipmentId: product.id,
            quantity: 1,
            startDate: dateRange.from.toISOString(),
            endDate: dateRange.to.toISOString(),
            endDateIsExact: true,
            unitPrice: product.price1Day || 0,
            locationId: location.id,
            pricingOptionType: PricingOptionType.Rental,
        }, {
            onSuccess: () => success(`${product.name} added to cart!`),
        });
    }, [isUnavailable, unavailableForDates, location, dateRange, product, addToCartMutation, warning, success]);

    const handleLocationSelected = useCallback((selectedLocation: CustomerLocationDto) => {
        setLocation(selectedLocation);
        if (dateRange.from && dateRange.to) {
            addToCartMutation.mutate({
                equipmentId: product.id,
                quantity: 1,
                startDate: dateRange.from.toISOString(),
                endDate: dateRange.to.toISOString(),
                endDateIsExact: true,
                unitPrice: product.price1Day || 0,
                locationId: selectedLocation.id,
                pricingOptionType: PricingOptionType.Rental,
            }, {
                onSuccess: () => success(`${product.name} added to cart!`),
            });
        }
    }, [dateRange, product, addToCartMutation, setLocation, success]);

    const renderPricing = () => {
        if (unavailableForDates && location) {
            return (
                <div>
                    <p className="text-sm text-muted-foreground">Try different dates for pickup in {location.name}.</p>
                    <div className="relative z-10 mt-1">
                        <DatePicker actionLabel="Change dates" ariaLabel={`Change dates for ${product.name}`} />
                    </div>
                </div>
            );
        }

        if (product.isRefurbished) {
            return (
                <div>
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Sale Price</p>
                    <p className="font-display text-2xl text-primary">{formatPrice(product.price1Day || 0)}</p>
                </div>
            );
        }

        const monthlyRate = getLowestMonthlyRentalRate(product);
        if (rentalMode === 'subscription' && monthlyRate !== null) {
            return (
                <div>
                    <p className="text-xs text-muted-foreground">Monthly rental from</p>
                    <p className="font-display text-2xl text-primary">
                        {formatPrice(monthlyRate)}<span className="text-sm"> / month</span>
                    </p>
                    <p className="text-xs text-muted-foreground">Choose a plan on the product page</p>
                </div>
            );
        }

        if (rentalDays && calculatedTier) {
            return (
                <div>
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
                        {rentalDays} day{rentalDays > 1 ? 's' : ''}
                    </p>
                    <p className="font-display text-2xl text-primary">{formatPrice(calculatedTier.price)}</p>
                    <p className="text-xs text-muted-foreground">{calculatedTier.tierName} rate</p>
                </div>
            );
        }

        if (lowestPrice) {
            return (
                <div>
                    <p className="font-display text-2xl text-primary">{formatPrice(lowestPrice)}</p>
                </div>
            );
        }

        return (
            <div>
                <Link
                    to={detailHref}
                    className="text-sm font-semibold text-primary hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                >
                    Select dates for pricing
                </Link>
            </div>
        );
    };

    // Show stock badges only for the selected location and complete date range.
    const renderTopBadge = () => {
        if (isUnavailable) {
            return (
                <div className="absolute left-3 top-3 rounded-full bg-foreground/80 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                    Unavailable
                </div>
            );
        }
        if (unavailableForDates && location) {
            return (
                <div className="absolute left-3 top-3 rounded-full bg-warning/90 px-2.5 py-1 text-[11px] font-semibold text-warning-foreground backdrop-blur-sm">
                    Unavailable for these dates
                </div>
            );
        }
        if (isLowStock) {
            return (
                <div className="absolute left-3 top-3 rounded-full bg-highlight px-2.5 py-1 text-[11px] font-semibold text-highlight-foreground shadow-sm">
                    Only {product.availableQuantity} left
                </div>
            );
        }
        return null;
    };

    const renderCornerBadge = () => {
        if (cannotAddToCart) return null;
        if (product.isPopular) {
            return (
                <div className="absolute right-3 top-3 rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground shadow-sm">
                    Popular
                </div>
            );
        }
        if (product.isFeatured) {
            return (
                <div className="absolute right-3 top-3 rounded-full bg-accent px-2.5 py-1 text-[11px] font-bold text-accent-foreground shadow-sm">
                    Featured
                </div>
            );
        }
        return null;
    };

    return (
        <article
            className={cn(
                // `content-visibility: auto` was here to skip rendering off-screen
                // cards as a perf optimisation. Removed because Firefox renders
                // those skipped cards as blank rectangles during fast scroll and
                // in full-page screenshots, even when the DOM has full content.
                // At our catalogue size (≤ ~50 products per page batch) the
                // optimisation isn't worth the visual bug.
                'group relative flex flex-col overflow-hidden rounded-[1.75rem] border border-border/80 bg-[linear-gradient(180deg,rgba(255,255,255,1)_0%,rgba(249,245,239,1)_100%)]',
                'shadow-card transition-[transform,box-shadow] duration-300',
                'hover:-translate-y-1 hover:shadow-[0_24px_50px_rgba(15,118,110,0.14)]',
                'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2'
            )}
            aria-labelledby={`product-${product.id}-title`}
        >
            {/* Image */}
            <div className="relative flex-shrink-0 overflow-hidden bg-secondary">
                <div className="absolute inset-x-0 bottom-0 z-10 h-24 bg-gradient-to-t from-background/60 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />
                <div className="aspect-[4/3] sm:aspect-[4/5] overflow-hidden">
                {product.primaryImageUrl ? (
                    <>
                        <img
                            src={product.primaryImageUrl}
                            alt={`${product.name} - ${product.shortDescription || 'baby equipment for rent'}`}
                            className={cn(
                                'h-full w-full object-contain object-center transition-[transform,opacity,filter] duration-700 ease-out',
                                secondaryImage ? 'group-hover:opacity-0' : 'group-hover:scale-[1.06]',
                                isUnavailable && 'opacity-60 grayscale-[20%]'
                            )}
                            loading="lazy"
                            decoding="async"
                        />
                        {secondaryImage && (
                            <img
                                src={secondaryImage}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 h-full w-full object-contain object-center opacity-0 transition-[transform,opacity] duration-700 ease-out group-hover:scale-[1.03] group-hover:opacity-100"
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </>
                ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground/45" aria-hidden="true">
                        <Package className="h-12 w-12" />
                        <span className="text-xs font-medium">No image</span>
                    </div>
                )}
                </div>

                {/* Status badges */}
                {renderTopBadge()}
                {renderCornerBadge()}

                {/* Quick Add requires an active product with stock for the selected dates. */}
                {!cannotAddToCart && (
                    <div className="absolute inset-x-0 bottom-0 z-20 hidden translate-y-full p-3 opacity-0 transition-[transform,opacity] duration-300 ease-out group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:translate-y-0 group-hover:opacity-100 md:block">
                        {hasCheckedDateContext ? (
                            <Button
                                className="delight-sheen min-h-[44px] w-full gap-2 rounded-[1rem] bg-accent text-sm font-bold text-accent-foreground shadow-lg transition-transform duration-200 hover:-translate-y-0.5 hover:bg-accent/90 active:scale-95 active:transition-none"
                                size="sm"
                                onClick={handleAddToCart}
                                aria-label={`Quickly add ${product.name} to cart`}
                                disabled={addToCartMutation.isPending}
                            >
                                {addToCartMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                ) : (
                                    <ShoppingCart className="h-4 w-4" aria-hidden="true" />
                                )}
                                {addToCartMutation.isPending ? 'Adding…' : 'Add to Cart'}
                            </Button>
                        ) : (
                            <Link
                                to={detailHref}
                                onClick={(e) => e.stopPropagation()}
                                className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[1rem] bg-white/95 px-4 text-sm font-bold text-primary shadow-lg transition-transform duration-200 hover:-translate-y-0.5"
                                aria-label={`View details for ${product.name}`}
                            >
                                View Product Details
                                <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                        )}
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col gap-2 p-5">
                {/* Brand / Age meta */}
                {(product.brand || product.ageRange) && (
                    <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary">
                        {[product.brand, product.ageRange].filter(Boolean).join(' · ')}
                    </p>
                )}

                {/* Name + Rating */}
                <div className="flex items-start justify-between gap-2">
                    <h3
                        id={`product-${product.id}-title`}
                        className="flex-1 font-display text-xl leading-tight text-foreground line-clamp-2"
                    >
                        <a
                            href={detailHref}
                            className="focus:outline-none focus-visible:underline"
                        >
                            <span aria-hidden="true" className="absolute inset-0" />
                            {product.name}
                        </a>
                    </h3>
                    {product.rating && (
                        <div
                            className="flex items-center gap-0.5 flex-shrink-0 mt-0.5"
                            aria-label={`Rating: ${product.rating} out of 5`}
                        >
                            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                            <span className="text-xs font-semibold text-muted-foreground">{product.rating}</span>
                        </div>
                    )}
                </div>

                {/* Pricing */}
                <div className="mt-auto border-t border-border/70 pt-4">
                    {renderPricing()}
                    {!isUnavailable && !hasFullContext && location && (
                        <div className="relative z-10 mt-1">
                            <DatePicker actionLabel="Choose dates to check availability" ariaLabel={`Choose dates for ${product.name}`} />
                        </div>
                    )}
                </div>

                {/* Mobile-first CTA: always visible on small screens (suppressed
                    when the product isn't addable from this card — see cannotAddToCart). */}
                {!cannotAddToCart && (
                    hasCheckedDateContext ? (
                        <Button
                            className="relative z-10 mt-3 min-h-[44px] w-full gap-2 rounded-[1rem] bg-accent text-sm font-bold text-accent-foreground transition-transform duration-200 hover:-translate-y-0.5 hover:bg-accent/90 active:scale-95 active:transition-none md:hidden"
                            size="sm"
                            onClick={handleAddToCart}
                            aria-label={`Add ${product.name} to cart`}
                            disabled={addToCartMutation.isPending}
                        >
                            {addToCartMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            ) : (
                                <ShoppingCart className="h-4 w-4" aria-hidden="true" />
                            )}
                            {addToCartMutation.isPending ? 'Adding…' : 'Add to Cart'}
                        </Button>
                    ) : (
                        <Link
                            to={detailHref}
                            onClick={(e) => e.stopPropagation()}
                            className="relative z-10 mt-3 flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-[1rem] border border-primary/25 bg-white text-sm font-semibold text-primary transition-transform duration-200 hover:-translate-y-0.5 md:hidden"
                            aria-label={`View details for ${product.name}`}
                        >
                            View Product Details
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                    )
                )}
            </div>

            <LocationRequiredModal
                isOpen={showLocationModal}
                onClose={() => setShowLocationModal(false)}
                onLocationSelected={handleLocationSelected}
                productName={product.name}
                equipmentTypeId={product.id}
                startDate={dateRange.from?.toISOString()}
                endDate={dateRange.to?.toISOString()}
            />
        </article>
    );
});
